import type {ChartOptions, TooltipItem} from 'chart.js';
import {useState} from 'react';
import {Pie} from 'react-chartjs-2';
import type {z} from 'zod';
import {
  COLOR_BLACK_3,
  COLOR_BLACK_4,
  COLOR_PIE_GRAPH_1,
} from '../../../../../config/constants/colorCodes';
import type {
  Bunri,
  Sex,
  UnivType,
  Universities,
} from '../../../../../core/entities/companyDetail/schema';
import {BarChart} from './BarChart';
import {
  convertSexData,
  convertUniversitiesData,
  sexBgColors,
} from './OfferedAttribute';
import {SectionTitle} from './SectionTitle';

type Props = {
  universities: z.infer<typeof Universities>;
  sex: z.infer<typeof Sex>;
  bunri: z.infer<typeof Bunri>;
  graduate: z.infer<typeof UnivType>;
  companyID: number;
  title: string;
};

type BunriCount = {
  bunri: string; // 文系 or 理系
  percent: number;
};

type GraduateCount = {
  graduate: string; // 学士 or 修士 or 博士
  percent: number;
};

const convertBunriData = (bunri: z.infer<typeof Bunri>): BunriCount[] => {
  const bunriCount: BunriCount[] = [
    {
      bunri: '文系',
      percent: bunri.bunkei ?? 0,
    },
    {
      bunri: '理系',
      percent: bunri.rikei ?? 0,
    },
  ];
  return bunriCount;
};

const convertGraduateData = (
  graduate: z.infer<typeof UnivType>,
): GraduateCount[] => {
  const graduateCount: GraduateCount[] = [
    {
      graduate: '学士',
      percent: graduate.bachelor ?? 0,
    },
    {
      graduate: '修士',
      percent: graduate.graduate ?? 0,
    },
    {
      graduate: '博士',
      percent: graduate.doctor ?? 0,
    },
  ];
  return graduateCount;
};

export const FollowedAttribute: React.FC<Props> = ({
  universities,
  sex,
  bunri,
  graduate,
  companyID,
  title,
}) => {
  const sexCount = convertSexData(sex);
  const univCount = convertUniversitiesData(universities);
  const bunriCount = convertBunriData(bunri);
  const graduateCount = convertGraduateData(graduate);
  const [univCountPage, _setUnivCountPage] = useState(0);

  const sexData = {
    labels: sexCount.map((data) => data.sex),
    datasets: [
      {
        data: sexCount.map((data) => data.percent),
        backgroundColor: sexBgColors(sexCount),
      },
    ],
  };

  const bunriBgColors = bunriCount
    .filter((data) => data.bunri === '文系' || data.bunri === '理系')
    .map((data) => {
      switch (data.bunri) {
        case '文系': {
          return COLOR_PIE_GRAPH_1;
        }

        case '理系': {
          return COLOR_BLACK_3;
        }

        default: {
          return '';
        }
      }
    });

  const bunriData = {
    labels: bunriCount.map((data) => data.bunri),
    datasets: [
      {
        data: bunriCount.map((data) => data.percent),
        backgroundColor: bunriBgColors,
      },
    ],
  };

  const graduateBgColors = graduateCount
    .filter(
      (data) =>
        data.graduate === '学士' ||
        data.graduate === '修士' ||
        data.graduate === '博士',
    )
    .map((data) => {
      switch (data.graduate) {
        case '学士': {
          return COLOR_PIE_GRAPH_1;
        }

        case '修士': {
          return COLOR_BLACK_3;
        }

        case '博士': {
          return COLOR_BLACK_4;
        }

        default: {
          return '';
        }
      }
    });

  const graduateData = {
    labels: graduateCount.map((data) => data.graduate),
    datasets: [
      {
        data: graduateCount.map((data) => data.percent),
        backgroundColor: graduateBgColors,
      },
    ],
  };

  const options: ChartOptions<'pie'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          filter(item, chart) {
            // 複数ページの場合現在開いているデータのみ表示
            if (chart.datasets[0] && chart.datasets[0]?.data.length > 5) {
              return (
                item.index! >= univCountPage * 5 &&
                item.index! < (univCountPage + 1) * 5
              );
            }

            return true;
          },
        },
      },
      tooltip: {
        callbacks: {
          label(tooltipItem: TooltipItem<'pie'>) {
            // グラフのパーセント表示
            const total = tooltipItem.dataset.data.reduce(
              (acc, val) => acc + val,
              0,
            );
            const percentage = `${(((tooltipItem.raw as number) / total) * 100).toFixed(1)}%`;
            return percentage;
          },
        },
      },
    },
  };

  return (
    <div className="mt-8 pc:mt-16">
      <SectionTitle
        id="follow_users_attribute"
        sectionTitle={title}
        companyID={companyID}
        showEntrysheetAndReport={true}
      />
      <div className="rounded-b-[12px] rounded-t-[4px] border-t-[1px] border-solid border-t-line-1 pc:border-t-[0px]">
        <div className="pc:mt-8 bg-white p-16">
          <h3 className="font-bold pc:p-8">大学別</h3>
          <BarChart data={univCount} />
        </div>
        <div className="pc:mt-8 pc:flex pc:justify-between items-center bg-white p-16 rounded-t-[4px] rounded-b-[12px]">
          <div className="pc:w-[30%] mt-16 pc:mt-0">
            <h3 className="font-bold p-8">男女比</h3>
            <Pie data={sexData} options={options} />
          </div>
          <div className="pc:w-[30%] mt-16 pc:mt-0">
            <h3 className="font-bold p-8">文系・理系</h3>
            <Pie data={bunriData} options={options} />
          </div>
          <div className="pc:w-[30%] mt-16 pc:mt-0">
            <h3 className="font-bold p-8">教育課程</h3>
            <Pie data={graduateData} options={options} />
          </div>
        </div>
        <p className="text-sm text-left mt-8">
          ※外資就活ドットコム会員の過去データ累積値
        </p>
      </div>
    </div>
  );
};
